import { create } from 'zustand';
import { TaskStatus, TaskPriority, TaskFilters } from '@/app/types/task';

interface TaskStoreState {
  // Filter state
  filters: TaskFilters;
  setFilters: (filters: Partial<TaskFilters>) => void;
  clearFilters: () => void;

  // Search query
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Quick create modal state
  isCreateModalOpen: boolean;
  openCreateModal: () => void;
  closeCreateModal: () => void;

  // Quick TODO modal state (minimal form in header)
  isQuickTodoModalOpen: boolean;
  openQuickTodoModal: () => void;
  closeQuickTodoModal: () => void;

  // Pre-filled data for quick create (e.g., when creating task for a specific party)
  prefilledPartyId?: number;
  setPrefilledPartyId: (partyId: number | undefined) => void;

  // Default task type context (set when opening create modal from a filtered page)
  defaultTaskType?: string;
  setDefaultTaskType: (taskType: string | undefined) => void;

  // Selected task for detail view
  selectedTaskId?: number;
  setSelectedTaskId: (taskId: number | undefined) => void;
}

const initialFilters: TaskFilters = {
  status: undefined,
  priority: undefined,
  assigned_to_user_id: undefined,
  parent_entity_type: undefined,
  parent_entity_id: undefined,
  task_category: undefined,
  due_date_from: undefined,
  due_date_to: undefined,
  search: undefined,
};

/**
 * Task Store
 *
 * Global state management for task-related UI state:
 * - Task filters (status, priority, assigned_to, etc.)
 * - Search query
 * - Create modal state
 * - Pre-filled data for quick actions
 * - Selected task for detail view
 */
export const useTaskStore = create<TaskStoreState>((set) => ({
  // Filter state
  filters: initialFilters,
  setFilters: (newFilters) =>
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    })),
  clearFilters: () => set({ filters: initialFilters }),

  // Search query
  searchQuery: '',
  setSearchQuery: (query) => set({ searchQuery: query }),

  // Quick create modal
  isCreateModalOpen: false,
  openCreateModal: () => set({ isCreateModalOpen: true }),
  closeCreateModal: () => set({ isCreateModalOpen: false, prefilledPartyId: undefined, defaultTaskType: undefined }),

  // Quick TODO modal (minimal form in header)
  isQuickTodoModalOpen: false,
  openQuickTodoModal: () => set({ isQuickTodoModalOpen: true }),
  closeQuickTodoModal: () => set({ isQuickTodoModalOpen: false }),

  // Prefilled data
  prefilledPartyId: undefined,
  setPrefilledPartyId: (partyId) => set({ prefilledPartyId: partyId }),

  // Default task type
  defaultTaskType: undefined,
  setDefaultTaskType: (taskType) => set({ defaultTaskType: taskType }),

  // Selected task
  selectedTaskId: undefined,
  setSelectedTaskId: (taskId) => set({ selectedTaskId: taskId }),
}));
